import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import EditIcon from '@material-ui/icons/Edit';
import QueueIcon from '@material-ui/icons/Queue';
import DeleteIcon from '@material-ui/icons/Delete';
import LinkMaterial from '@material-ui/core/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';

import InputControl from '~/components/Input/InputControl';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import InputSelect from '~/components/Input/InputSelect';
import Paper from '~/components/Paper';
import Dialog from '~/components/Dialog';
import GridItem from '~/components/GridItem';
import GridContainer from '~/components/GridContainer';
import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import InputSwitch from '~/components/Input/InputSwitch';
import IconButton from '~/components/Button/IconButton';
import ModalDelete from '~/components/ModalDelete';
import ModalAction from '~/components/Modal/ModalAction';
import InputSearch from '~/components/Input/InputSearch';
import TablePagination from '~/components/Table/TablePagination';
import Button from '~/components/Button';
import LinearProgress from '~/components/LinearProgress';

import maskCellPhone from '~/utils/maskCellPhone';
import isPresent from '~/utils/isPresent';
import maskCpf from '~/utils/maskCpf';
import toInt from '~/utils/Number/toInt';

import Request from '~/providers/Request';
import { showPerson, editPerson } from '~/config/routes';
import { packageOptions } from '~/config/constants';
import { fetchUsers } from '~/utils/fetchUsers';

const PanelUsers = ({ corporation }) => {
  const { t } = useTranslation();
  const classesTable = tableStyles();

  const [isEmergency, setIsEmergency] = useState(false);
  const [isDriving, setIsDriving] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);
  const [isDoctorAll, setIsDoctorAll] = useState(false);
  const [isPlan, setIsPlan] = useState(false);

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [userRemove, setUserRemove] = useState();
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [loadingPackage, setLoadingPackage] = useState(false);
  const [packageActive, setPackageActive] = useState(null);
  const [enable, setEnable] = useState(null);
  const [userToPackage, setUserToPackage] = useState(null);

  const [showModalAddUser, setShowModalAddUser] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [user, setUser] = useState(null);
  const [packs, setPack] = useState([]);
  const [avaliablePackages, setAvaliablePackages] = useState([]);
  const [packPackages, setPackPackages] = useState([]);
  const [packsOptions, setPacksOptions] = useState([]);

  async function getUsers() {
    setLoading(true);

    Request.get(
      `/admin/corporation/${corporation.id}/users`,
      { qs: inputSearch, page: page + 1, per_page: rowsPerPage },
      { normalizeJSON: false }
    )
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setUsers(data);
        setPage(toInt(meta.page) - 1);
        setTotalUsers(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function onDelete() {
    setLoadingDelete(true);

    Request.del(
      `/admin/corporation/${corporation.id}/users/${userRemove.id}`,
      null,
      { normalizeJSON: false }
    )
      .then(() => {
        const newUsers = users.filter((p) => p.id !== userRemove.id);
        setUsers(newUsers);
        setTotalUsers(totalUsers - 1);
        setUserRemove(null);
        setLoadingDelete(false);
        toast.success(t('delete-toast-success'));
      })
      .catch((e) => {
        console.error(e);
        setLoadingDelete(false);
      });
  }

  async function handlePackage() {
    setLoadingPackage(true);

    const pack = {
      pack_type: packageActive,
    };

    const type = enable ? 'enable_package' : 'disable_package';

    Request.post(
      `/admin/corporation/${corporation.id}/users/${userToPackage.id}/${type}`,
      pack,
      { normalizeJSON: false }
    )
      .then(() => {
        const objIndex = users.findIndex(
          (user) => user.id === userToPackage.id
        );

        if (!enable) {
          const newPacks = users[objIndex].package_users.filter(
            (pack) => pack.pack_type !== packageActive
          );
          users[objIndex].package_users = newPacks;
        }
        if (enable) {
          users[objIndex].package_users.push({ pack_type: packageActive });
        }
        setLoadingPackage(false);
        setPackageActive(null);
        setEnable(null);
        setUserToPackage(null);

        toast.success(
          t(
            `corporation.extras.${
              enable ? 'activePackageSuccess' : 'removePackageSuccess'
            }`
          )
        );
      })
      .catch((e) => {
        console.error(e);
        setLoadingPackage(false);
      });
  }

  const handleSave = () => {
    if (user && user.id) {
      setLoadingSave(true);

      const packages = {};
      if (packs) {
        packs.map((pack) => {
          packages[pack.id] = true;
          return pack;
        });
      }

      let packPackagesIds = [];
      if (packPackages) {
        packPackagesIds = packPackages.map((packPackage) => {
          return packPackage.id;
        });
      }

      packages['packPackages'] = packPackagesIds;

      const data = {
        user_id: user.id,
        packs: packages,
      };

      Request.post(`/admin/corporation/${corporation.id}/users`, data, null, {
        normalizeJSON: false,
      })
        .then(() => {
          setLoadingSave(false);
          setUser(null);
          setPack(null);
          setShowModalAddUser(false);
          getUsers();
          toast.success(t('corporation.extras.addUserSuccess'));
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('corporation.extras.addUserError'));
          setLoadingSave(false);
        });
    } else {
      toast.error(t('corporation.extras.selectUser'));
    }
  };

  useEffect(() => {
    getUsers();
  }, [inputSearch, page, rowsPerPage]);

  useEffect(() => {
    async function getAvaliablePackages(corporation) {
      const activPacks = [];
      if (corporation.is_doctor) {
        setIsDoctor(true);
        activPacks.push('doctor');
      }
      if (corporation.is_doctor_all) {
        setIsDoctorAll(true);
        activPacks.push('doctor_all');
      }
      if (corporation.is_driving) {
        setIsDriving(true);
        activPacks.push('driving');
      }
      if (corporation.is_emergency) {
        setIsEmergency(true);
        activPacks.push('emergency');
      }
      if (corporation.is_plan) {
        setIsPlan(true);
        activPacks.push('users');
      }

      setAvaliablePackages(activPacks);
    }

    async function fetchPacks() {
      Request.get('/admin/packs', null, { normalizeJSON: true })
        .then((response) => {
          setPacksOptions(response.data);
        })
        .catch((e) => {
          console.log(e);
          setPacksOptions([]);
        });
    }

    getAvaliablePackages(corporation);
    fetchPacks();
  }, [corporation]);

  return (
    <Paper
      Icon={AssignmentIndIcon}
      iconColor="purple"
      title={t('corporation.extras.users')}
    >
      <GridContainer>
        <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
          <InputSearch
            style={{ marginBottom: 20 }}
            value={inputSearch}
            label={t('action.search')}
            placeholder={t('action.search')}
            onChange={setInputSearch}
          />
        </GridItem>
        <GridItem
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          style={{ display: 'block', textAlign: 'right' }}
        >
          <Button
            onClick={() => {
              setShowModalAddUser(true);
            }}
          >
            {t('corporation.extras.addUser')}
          </Button>
          <Typography
            variant="subtitle2"
            color="primary"
            align="right"
            style={{ paddingRight: 16, marginTop: 10 }}
          >
            <strong>Total: </strong>
            {totalUsers}
          </Typography>
        </GridItem>
      </GridContainer>

      <LinearProgress loading={loading} />

      <Table
        emptyData={users.length === 0}
        loading={loading}
        columns={
          <>
            <TableCell className={classesTable.cell}>
              {t('action.actions')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('person.attributes.name')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('person.attributes.cpf')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('person.attributes.cell_phone')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('person.attributes.email')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('constants.packages.doctor')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('constants.packages.doctor_all')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('constants.packages.driving')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('constants.packages.emergency')}
            </TableCell>
            <TableCell className={classesTable.cell}>
              {t('constants.packages.users')}
            </TableCell>
          </>
        }
      >
        {users.map((row) => {
          return (
            <TableRow key={row.id} hover className={classesTable.row}>
              <TableCell>
                <Link to={showPerson.reverse({ id: row.id })}>
                  <IconButton
                    tooltip={t('action.show')}
                    Icon={RemoveRedEyeIcon}
                    iconColor="green"
                  />
                </Link>
                <LinkMaterial
                  target="_blank"
                  rel="noopener"
                  href={showPerson.reverse({ id: row.id })}
                >
                  <IconButton
                    tooltip={t('action.show')}
                    Icon={OpenInNewIcon}
                    iconColor="primary"
                  />
                </LinkMaterial>
                <Link to={editPerson.reverse({ id: row.id })}>
                  <IconButton
                    tooltip={t('action.edit')}
                    Icon={EditIcon}
                    iconColor="orange"
                  />
                </Link>
                <IconButton
                  tooltip={t('action.delete')}
                  onClick={() => {
                    setUserRemove(row);
                  }}
                  Icon={DeleteIcon}
                  iconColor="red"
                />
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{maskCpf(row.cpf)}</TableCell>
              <TableCell>{maskCellPhone(row.cell_phone)}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>
                <InputSwitch
                  value={
                    row.package_users.filter(
                      (element) => element.pack_type === 'doctor'
                    ).length > 0
                  }
                  disabled={!isDoctor}
                  onChange={(value) => {
                    setUserToPackage(row);
                    setPackageActive('doctor');
                    setEnable(value);
                  }}
                />
              </TableCell>
              <TableCell>
                <InputSwitch
                  value={
                    row.package_users.filter(
                      (element) => element.pack_type === 'doctor_all'
                    ).length > 0
                  }
                  disabled={!isDoctorAll}
                  onChange={(value) => {
                    setUserToPackage(row);
                    setPackageActive('doctor_all');
                    setEnable(value);
                  }}
                />
              </TableCell>
              <TableCell>
                <InputSwitch
                  value={
                    row.package_users.filter(
                      (element) => element.pack_type === 'driving'
                    ).length > 0
                  }
                  disabled={!isDriving}
                  onChange={(value) => {
                    setUserToPackage(row);
                    setPackageActive('driving');
                    setEnable(value);
                  }}
                />
              </TableCell>
              <TableCell>
                <InputSwitch
                  value={
                    row.package_users.filter(
                      (element) => element.pack_type === 'emergency'
                    ).length > 0
                  }
                  disabled={!isEmergency}
                  onChange={(value) => {
                    setUserToPackage(row);
                    setPackageActive('emergency');
                    setEnable(value);
                  }}
                />
              </TableCell>
              <TableCell>
                <InputSwitch
                  value={
                    row.package_users.filter(
                      (element) => element.pack_type === 'users'
                    ).length > 0
                  }
                  disabled={!isPlan}
                  onChange={(value) => {
                    setUserToPackage(row);
                    setPackageActive('users');
                    setEnable(value);
                  }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </Table>

      <LinearProgress loading={loading} />

      <TablePagination
        count={totalUsers}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <ModalDelete
        modal={isPresent(userRemove)}
        isLoading={loadingDelete}
        message={t('corporation.extras.removeUser')}
        buttonText={t('action.remove')}
        deleteAction={onDelete}
        onClose={() => {
          setUserRemove(null);
        }}
      />

      <ModalAction
        open={isPresent(userToPackage)}
        loading={loadingPackage}
        Icon={enable ? QueueIcon : DeleteIcon}
        iconColor={enable ? 'green' : 'red'}
        text={t(
          `corporation.extras.${
            enable ? 'activePackageUser' : 'removePackageUser'
          }`,
          {
            package: t(`constants.packages.${packageActive}`),
          }
        )}
        buttonColor={enable ? 'green' : 'red'}
        buttonText={enable ? t('action.active') : t('action.deactivate')}
        onAction={handlePackage}
        onClose={() => {
          setUserToPackage(null);
        }}
      />

      <Dialog
        icon={<PlaylistAddIcon />}
        isOpen={showModalAddUser}
        handleClose={() => setShowModalAddUser(false)}
        title={t('corporation.extras.addUserRelation')}
        dialogActions={
          <Button onClick={handleSave} disabled={loadingSave} color="primary">
            {t('action.save')}
          </Button>
        }
      >
        <div style={{ minWidth: 560 }}>
          <LinearProgress loading={loadingSave} />
          <GridContainer>
            <GridItem xs={12}>
              <InputControl
                required
                name={'user'}
                label={t('corporation.attributes.user')}
                value={user}
                inputComponent={InputSelectAsync}
                onChange={({ user }) => setUser(user)}
                InputProps={{
                  isClearable: true,
                  handleFetch: fetchUsers,
                  createEnabled: false,
                  error: false,
                }}
              />
            </GridItem>
            <GridItem xs={12}>
              <InputControl
                name={'packs'}
                label={t('corporation.attributes.packs')}
                value={packs}
                inputComponent={InputSelect}
                onChange={({ packs }) => {
                  setPack(packs);
                }}
                InputProps={{
                  isClearable: true,
                  isMulti: true,
                  options: Object.keys(packageOptions)
                    .filter((key) => avaliablePackages.includes(key))
                    .map((key) => ({ id: key, name: packageOptions[key] })),
                }}
              />
            </GridItem>
            <GridItem xs={12}>
              <InputControl
                name={'packPackages'}
                label={t('corporation.attributes.packPackages')}
                value={packPackages}
                inputComponent={InputSelect}
                onChange={({ packPackages }) => {
                  setPackPackages(packPackages);
                }}
                InputProps={{
                  isClearable: true,
                  isMulti: true,
                  options: packsOptions,
                }}
              />
            </GridItem>
          </GridContainer>
          <LinearProgress loading={loadingSave} />
        </div>
      </Dialog>
    </Paper>
  );
};

PanelUsers.propTypes = {
  corporation: PropTypes.object,
};

export default React.memo(PanelUsers);
